import { CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button, Field, IconButton, TextInput } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { acceptInvite, claimInvites, getInvite, InviteDetails } from '@/services/api/invites';
import { validatePassword } from '@/utils/validators';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuthContext();

  // DEV ONLY: set to true to preview the form without a real invite token
  const DEV_MOCK = import.meta.env.DEV && !token;
  const mockInvite: InviteDetails = {
    email: 'preview@hauserhq.com',
    firstName: 'James',
    lastName: 'Choe',
    accountExists: false,
    expired: false,
    status: 'pending',
    role: 'Buyer',
    authProvider: 'email',
  };

  const [invite, setInvite] = useState<InviteDetails | null>(DEV_MOCK ? mockInvite : null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!DEV_MOCK);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (DEV_MOCK) return;
    if (!token) {
      setLoadError('Invalid or missing invite link.');
      setIsLoading(false);
      return;
    }

    getInvite(token)
      .then((data) => {
        if (data.status === 'accepted') {
          setLoadError('This invite has already been used. Please sign in.');
        } else if (data.expired || data.status === 'expired') {
          setLoadError('This invite has expired. Please ask your advisor to resend it.');
        } else {
          setInvite(data);
        }
      })
      .catch(() => {
        setLoadError('This invite link is invalid or has expired.');
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  // If the user is already authenticated and the invite email matches, auto-claim and redirect
  useEffect(() => {
    if (!user || !invite || !invite.accountExists) return;
    if (user.email?.toLowerCase() !== invite.email.toLowerCase()) return;

    claimInvites()
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((err) => {
        console.warn('Auto-claim failed:', err);
        setSubmitError('We could not link your invite automatically. Please sign in to continue.');
      });
  }, [user, invite, navigate]);

  const validationSchema = yup.object({
    password: yup
      .string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required')
      .test('isValidPassword', 'Password does not meet the requirements', validatePassword),
    confirmPassword: yup
      .string()
      .required('Please confirm your password')
      .oneOf([yup.ref('password')], 'Passwords do not match'),
  });

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      if (!token) return;
      try {
        setSubmitError(null);
        await acceptInvite(token, values.password);
        navigate('/login', {
          state: { email: invite?.email },
          replace: true,
        });
      } catch (err: any) {
        const code = err?.response?.data?.code;
        if (code === 'account_exists') {
          // Backend fulfilled property access — redirect to login
          navigate('/login', { state: { email: invite?.email }, replace: true });
          return;
        }
        const message =
          err?.response?.data?.message ?? err?.message ?? 'Something went wrong. Please try again.';
        setSubmitError(message);
      }
    },
  });

  const loginUrl = `/login?returnTo=${encodeURIComponent(`/accept-invite?token=${token ?? ''}`)}`;

  const passwordInput = (props: {
    name: 'password' | 'confirmPassword';
    show: boolean;
    onToggle: () => void;
    toggleLabel: string;
    placeholder: string;
    autoFocus?: boolean;
  }) => (
    <TextInput
      id={`invite-${props.name}`}
      type={props.show ? 'text' : 'password'}
      name={props.name}
      required
      placeholder={props.placeholder}
      autoComplete="new-password"
      autoFocus={props.autoFocus}
      value={formik.values[props.name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[props.name] && Boolean(formik.errors[props.name])}
      inputProps={{ 'aria-label': props.placeholder }}
      endAdornment={
        <IconButton
          icon={props.show ? 'eye-off' : 'eye'}
          label={props.toggleLabel}
          size="sm"
          variant="ghost"
          tipPlacement={false}
          onClick={props.onToggle}
        />
      }
      sx={{ pr: '4px' }}
    />
  );
  const fieldError = (msg?: string | false) => (msg ? <span className="hx-auth-field-error">{msg}</span> : null);
  const passwordError = formik.touched.password && formik.errors.password;
  const confirmError = formik.touched.confirmPassword && formik.errors.confirmPassword;

  const shellTitle = isLoading
    ? 'Invitation'
    : loadError
      ? 'Invitation unavailable'
      : invite?.accountExists
        ? 'Sign in'
        : 'Create account';

  return (
    <AuthShell
      title={shellTitle}
      footer={
        !isLoading &&
        invite &&
        !invite.accountExists && (
          <>
            Already have an account?{' '}
            <Link className="hx-auth-link" to="/login">
              Sign in
            </Link>
          </>
        )
      }>
      <style>{`.hx-auth-field-error { font-size: 11.5px; font-weight: 500; color: #DB5282; }`}</style>

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <CircularProgress size={28} sx={{ color: 'rgba(247, 247, 246, 0.6)' }} />
        </div>
      )}

      {!isLoading && loadError && (
        <>
          <h1 className="hx-auth-title">Invitation unavailable</h1>
          <p className="hx-auth-error" style={{ marginBottom: 22 }}>
            {loadError}
          </p>
          <Link className="hx-auth-link" to="/login" style={{ fontSize: 13 }}>
            Back to sign in
          </Link>
        </>
      )}

      {!isLoading && invite && invite.accountExists && (
        <>
          <h1 className="hx-auth-title">Welcome back{invite.firstName ? `, ${invite.firstName}` : ''}</h1>
          <p className="hx-auth-sub">You already have an account. Sign in to access your groups and properties.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {submitError && <p className="hx-auth-error">{submitError}</p>}
            <Button variant="primary" block onClick={() => navigate(loginUrl)}>
              {invite.authProvider === 'google' ? 'Sign in with Google' : 'Sign in'}
            </Button>
            <p className="hx-auth-sub" style={{ margin: 0, textAlign: 'center' }}>
              {invite.email}
            </p>
          </div>
        </>
      )}

      {!isLoading && invite && !invite.accountExists && (
        <form onSubmit={formik.handleSubmit}>
          <h1 className="hx-auth-title">
            {invite.firstName
              ? `Welcome, ${invite.firstName}${invite.lastName ? ` ${invite.lastName}` : ''}`
              : 'Create your account'}
          </h1>
          <p className="hx-auth-sub">
            {invite.firstName ? 'Create your account to accept the invitation.' : 'Set a password to accept the invitation.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Email" htmlFor="invite-email">
              <TextInput
                id="invite-email"
                value={invite.email}
                disabled
                autoComplete="email"
                inputProps={{ 'aria-label': 'Email' }}
              />
            </Field>
            <Field
              label="Password"
              htmlFor="invite-password"
              hint={passwordError ? undefined : 'Min. 8 characters, uppercase, lowercase, number, and symbol'}>
              {passwordInput({
                name: 'password',
                show: showPassword,
                onToggle: () => setShowPassword((s) => !s),
                toggleLabel: 'Toggle password visibility',
                placeholder: 'Password',
                autoFocus: true,
              })}
              {fieldError(passwordError)}
            </Field>
            <Field label="Confirm password" htmlFor="invite-confirmPassword">
              {passwordInput({
                name: 'confirmPassword',
                show: showConfirm,
                onToggle: () => setShowConfirm((s) => !s),
                toggleLabel: 'Toggle confirm password visibility',
                placeholder: 'Confirm password',
              })}
              {fieldError(confirmError)}
            </Field>
            {submitError && <p className="hx-auth-error">{submitError}</p>}
            <Button
              type="submit"
              variant="primary"
              block
              disabled={
                formik.isSubmitting || !formik.isValid || !formik.values.password || !formik.values.confirmPassword
              }>
              {formik.isSubmitting ? 'Creating account…' : 'Create account'}
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default AcceptInvite;
